// client/src/features/quotations/pendingSupplierVatPick.ts
//
// Answering "Does this item have VAT?" for a PENDING supplier-dropdown pick —
// a lookup-panel supplier option that prices a Master Catalogue row whose
// `vatApplicable` is blank, held UNWRITTEN by
// `QuotationDetailPage.handleSupplierSelect` while `CatalogueVatPrompt` asks
// (`pendingSupplierVatPick` state, that function's own comment). Split out,
// DEPENDENCY-INJECTED for its network calls, so this can be unit-tested
// without rendering `QuotationDetailPage` (a god file under a line ceiling,
// TECH_DEBT #26) — the same shape `answerCatalogueVatPrompt.ts` and
// `pendingCataloguePickVat.ts` already use for the OTHER two VAT-answer
// entry points.
//
// ── THE BUG THIS CLOSES ──────────────────────────────────────────────────
// The OLD flow wrote the vendor to the line FIRST, then answered the VAT
// question AFTER, through the page's per-line Refresh (↻) — a NAME-based
// re-lookup that re-runs pricing from scratch and can land on a DIFFERENT
// supplier than the one just picked here (a stale search, a since-changed
// price elsewhere). Asking first and re-fetching the SAME line's supplier
// options — the exact call `ItemLookupPanel` itself uses
// (`fetchSupplierDropdownDataFromAllTables`) — then matching the SAME
// `masterCatalogueId`, is what keeps the supplier the user actually chose.
//
// NEVER multiply by the VAT rate client-side: `SupplierDropdownItem
// .masterCatalogueId`'s own doc comment says the server already serves a
// `vatApplicable: true` row's `unitPrice`/`lineUnitPrice` VAT-inclusive, at
// the Settings rate. This function only re-fetches and matches; the money
// arithmetic stays entirely server-side.

import type { SupplierDropdownItem } from '../../lib/vendorInvoiceApi';

export interface PendingSupplierVatPickDeps {
  setCatalogueVat: (id: string, vatApplicable: boolean) => Promise<unknown>;
  /** The SAME fetch `ItemLookupPanel` uses — never a second lookup routine. */
  fetchSupplierOptions: (
    itemName?: string,
    quotationId?: string,
    lineUom?: string
  ) => Promise<SupplierDropdownItem[]>;
}

export interface PendingSupplierVatPickArgs {
  /** The option the user picked, before any answer — its own doc comment on `handleSupplierSelect`. */
  supplierData: SupplierDropdownItem;
  vatApplicable: boolean;
  quotationId?: string;
}

/**
 * @returns the RE-FETCHED option carrying the answered row's price — the
 *   caller applies it through the SAME write (`QuotationDetailPage
 *   .applySupplierPick`) any supplier pick uses, never a second one.
 * @throws when there is no `masterCatalogueId` to answer for, or the option
 *   is gone from the re-fetched list (the row was removed, or the search
 *   no longer surfaces it) — refuse, never guess (invariant 8). The VAT
 *   answer is still saved on the catalogue row even when this throws: only
 *   the re-fetch-and-apply is refused.
 */
export async function resolvePendingSupplierVatPick(
  args: PendingSupplierVatPickArgs,
  deps: PendingSupplierVatPickDeps
): Promise<SupplierDropdownItem> {
  const { supplierData, vatApplicable, quotationId } = args;
  if (!supplierData.masterCatalogueId) {
    throw new Error('This option has no Master Catalogue row to answer VAT for.');
  }

  await deps.setCatalogueVat(supplierData.masterCatalogueId, vatApplicable);

  const refreshed = await deps.fetchSupplierOptions(
    supplierData.description,
    quotationId,
    supplierData.lineUnit || undefined
  );
  const found = refreshed.find((o) => o.masterCatalogueId === supplierData.masterCatalogueId);
  if (!found) {
    throw new Error(
      'This option could not be found after the VAT answer was saved. Reload the page and try again.'
    );
  }
  return found;
}
